import { LatLngModel, MapAddressModel } from "@/types/common";
import { useMapsLibrary } from "@vis.gl/react-google-maps";
import { postcodeValidator } from "postcode-validator";
import { useMemo } from "react";

export const useLocation = () => {
  const geocodingLib = useMapsLibrary("geocoding");

  const geocoder = useMemo(() => {
    return geocodingLib && new geocodingLib.Geocoder();
  }, [geocodingLib]);

  async function getCurrentLocation(): Promise<LatLngModel | undefined> {
    if (navigator && navigator.geolocation) {
      return new Promise((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(
          (position) =>
            resolve({
              lat: position.coords.latitude,
              lng: position.coords.longitude,
            }),
          reject
        );
      });
    }
  }

  async function getAddressDetail(
    location: LatLngModel
  ): Promise<MapAddressModel> {
    if (geocoder) {
      const { results } = await geocoder.geocode({
        location,
      });
      if (results[0]) {
        const adrs_comp = results[0].formatted_address;
        const value = adrs_comp.split(",");
        const addressDetail = {
          city: value[value.length - 3],
          streetName: value[value.length - 4],
          county: value[value.length - 2],
          postCode: results[0].address_components?.find(
            (addr) => addr.types[0] === "postal_code"
          )?.short_name,
          country: value[value.length - 1],
          countryCode: results[0].address_components?.find(
            (addr) => addr.types[0] === "country"
          )?.short_name,
        };
        return {
          ...addressDetail,
          address: [
            addressDetail.streetName,
            addressDetail.city,
            addressDetail.county,
            addressDetail.country,
            addressDetail.postCode,
          ]
            .filter((c) => c)
            .join(","),
        };
      }
    }

    return {};
  }

  async function getLocationFromAddress(
    address: MapAddressModel
  ): Promise<LatLngModel | undefined> {
    const encodedAddress = encodeURIComponent(
      `${address.postCode} ${address.country ? address.country : ""}`
    );
    if (geocoder) {
      const { results } = await geocoder.geocode({
        address: encodedAddress,
      });
      if (results[0]) {
        return {
          lat: results[0].geometry.location.lat(),
          lng: results[0].geometry.location.lng(),
        };
      }
    }
    return;
  }

  return {
    getAddressDetail,
    getCurrentLocation,
    getLocationFromAddress,
    geocoder,
  };
};

export function verifyPostcode({
  postCode,
  countryCode,
}: MapAddressModel): boolean {
  if (postCode && countryCode) return postcodeValidator(postCode, countryCode);

  return false;
}

export function mapLocationToString(location?: LatLngModel): string {
  return location ? `${location.lat},${location.lng}` : "";
}

export function mapStringToLocation(
  location?: string
): LatLngModel | undefined {
  if (location) {
    const locationArr = location.split(",");
    if (locationArr.length >= 2) {
      return {
        lat: parseFloat(locationArr[0]),
        lng: parseFloat(locationArr[1]),
      };
    }
  }

  return;
}

export const getCountries = (lang = "en") => {
  const A = 65;
  const Z = 90;
  const countryName = new Intl.DisplayNames([lang], { type: "region" });
  const countries = [];
  for (let i = A; i <= Z; ++i) {
    for (let j = A; j <= Z; ++j) {
      let code = String.fromCharCode(i) + String.fromCharCode(j);
      let name = countryName.of(code);
      countries.push({ text: name, value: code });
    }
  }
  return countries;
};
